import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IProduto } from "shared/interfaces/IProduto";
import { IDeliveryTrackingResponse } from "shared/interfaces/IDelivery";
import { buscarProdutoPorId } from "shared/services/api/produto.api";
import {
  buscarDeliveryPedido,
  buscarDeliveryPedidoLoja,
} from "shared/services/api/delivery.api";
import { formatarMoeda, reaisParaCentavos } from "shared/utils/money";
import { getStatusStyle } from "shared/utils/compraStatus";
import styles from "./ModalDetalhes.module.scss";

interface PaymentDetails {
  type?: string;
  qrCode?: string;
  qrCodeUrl?: string;
}

interface ItemPedido {
  productId?: string;
  id?: string;
  productName?: string;
  nome?: string;
  thumbnailUrl?: string;
  productImage?: string;
  imagem?: string;
  quantity?: number;
  quantidade?: number;
  unitPrice?: number;
  preco?: number;
  total?: number;
  variationText?: string | null;
  internalSku?: string | null;
  sellerSku?: string | null;
}

interface ModalDetalhesProps {
  productId: string;
  orderId?: string;
  itens?: ItemPedido[];
  quantidade?: number;
  valorUnitario?: number;
  valorTotal?: number;
  formaPagamento?: string;
  paymentDetails?: PaymentDetails;
  subtotal?: number;
  shipping?: number;
  interest?: number;
  discount?: number;
  numeroPedido?: string;
  status?: string;
  tipo: "compra" | "venda";
  onClose?: () => void;
  modoPagina?: boolean;
}

const formatarPagamento = (pagamento?: string) => {
  switch (pagamento) {
    case "CREDIT_CARD":
      return "Cartão de crédito";
    case "DEBIT_CARD":
      return "Cartão de débito";
    case "PIX":
      return "PIX";
    case "BOLETO":
      return "Boleto";
    default:
      return pagamento || "Não informado";
  }
};

const formatarValor = (valor?: number) => {
  if (valor === undefined || valor === null) return "Não informado";
  return formatarMoeda(reaisParaCentavos(valor));
};

const formatarStatus = (status?: string) => {
  if (!status) return "Não informado";

  const statusMap: Record<string, string> = {
    PENDING: "Aguardando",
    DISTRIBUTING: "Distribuindo",
    AWAITING_ACCEPTANCE: "Aguardando aceitação",
    ACCEPTED: "Aceito",
    ARRIVED_AT_PICKUP: "Chegou para retirada",
    ON_HOLD: "Em espera",
    IN_PROGRESS: "Em andamento",
    COMPLETED: "Concluído",
    NOT_ATTENDED: "Não atendido",
    CANCELLED: "Cancelado",
  };

  return statusMap[status] || status;
};

export const ModalDetalhes: React.FC<ModalDetalhesProps> = ({
  productId,
  orderId,
  itens = [],
  quantidade,
  valorUnitario,
  valorTotal,
  formaPagamento,
  paymentDetails,
  subtotal,
  shipping,
  interest,
  discount,
  numeroPedido,
  status,
  tipo,
}) => {
  const statusInfo = getStatusStyle(status || "");
  const [produtos, setProdutos] = useState<Record<string, IProduto>>({});
  const [loading, setLoading] = useState(true);
  const [delivery, setDelivery] = useState<IDeliveryTrackingResponse | null>(
    null,
  );
  const navigate = useNavigate();

  const itensPedido = useMemo(() => {
    if (itens.length > 0) return itens;

    if (
      !productId &&
      quantidade === undefined &&
      valorUnitario === undefined &&
      valorTotal === undefined
    ) {
      return [];
    }

    return [
      {
        productId,
        quantity: quantidade,
        unitPrice: valorUnitario,
        total: valorTotal,
      },
    ];
  }, [itens, productId, quantidade, valorUnitario, valorTotal]);

  useEffect(() => {
    let ativo = true;

    const carregarProdutos = async () => {
      const ids = [
        ...new Set(
          itensPedido.map((item) => item.productId).filter(Boolean) as string[],
        ),
      ];

      if (!ids.length) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const resultados = await Promise.all(
          ids.map(async (id) => {
            try {
              return [id, await buscarProdutoPorId(id)] as const;
            } catch {
              return null;
            }
          }),
        );

        if (!ativo) return;

        const mapa: Record<string, IProduto> = {};
        resultados.forEach((resultado) => {
          if (!resultado) return;
          const [id, produto] = resultado;
          if (produto) mapa[id] = produto;
        });

        setProdutos(mapa);
      } finally {
        if (ativo) setLoading(false);
      }
    };

    carregarProdutos();
    return () => {
      ativo = false;
    };
  }, [itensPedido]);

  useEffect(() => {
    let ativo = true;

    const carregarDelivery = async () => {
      if (!orderId) {
        setDelivery(null);
        return;
      }

      try {
        const resultado =
          tipo === "compra"
            ? await buscarDeliveryPedido(orderId)
            : await buscarDeliveryPedidoLoja(orderId);

        if (ativo) setDelivery(resultado);
      } catch {
        if (ativo) setDelivery(null);
      }
    };

    carregarDelivery();
    return () => {
      ativo = false;
    };
  }, [orderId, tipo]);

  const subtotalCalculado = useMemo(() => {
    if (subtotal !== undefined) return subtotal;
    if (!itensPedido.length) return valorTotal ?? 0;

    return itensPedido.reduce((total, item) => {
      const qtd = item.quantity ?? item.quantidade ?? 1;
      const unitario = item.unitPrice ?? item.preco ?? 0;
      return total + (item.total ?? unitario * qtd);
    }, 0);
  }, [itensPedido, subtotal, valorTotal]);

  const totalCalculado =
    valorTotal !== undefined
      ? valorTotal
      : subtotalCalculado + (shipping ?? 0) + (interest ?? 0) - (discount ?? 0);

  const abrirProduto = (item: ItemPedido) => {
    if (!item.productId) return;
    navigate(`/produto/${item.productId}`);
  };

  const obterImagem = (item: ItemPedido) => {
    const produto = item.productId ? produtos[item.productId] : undefined;
    return (
      produto?.gallery?.[0] ||
      item.thumbnailUrl ||
      item.productImage ||
      item.imagem ||
      "/imagens/foto.png"
    );
  };

  const obterNome = (item: ItemPedido) => {
    const produto = item.productId ? produtos[item.productId] : undefined;
    return produto?.name || item.productName || item.nome || "Produto";
  };

  return (
    <div className={styles.conteudo}>
      <header className={styles.cabecalho}>
        <div className={styles.identificacaoPedido}>
          <span className={styles.label}>Detalhes do pedido</span>
          <div className={styles.linhaPedido}>
            <h2>#{numeroPedido || "Não informado"}</h2>
            {status && (
              <span
                className={`${styles.status} ${styles[statusInfo.className] || ""}`}
              >
                {statusInfo.label}
              </span>
            )}
          </div>
        </div>
        <div className={styles.totalTopo}>
          <span>Valor total</span>
          <strong>{formatarValor(totalCalculado)}</strong>
        </div>
      </header>

      <section className={styles.secao}>
        <div className={styles.tituloSecao}>
          <span>SEU PEDIDO</span>
          <h3>Produtos</h3>
        </div>

        <div className={styles.lista}>
          {loading ? (
            <p className={styles.estado}>Carregando produtos...</p>
          ) : itensPedido.length === 0 ? (
            <p className={styles.estado}>
              Nenhum produto encontrado neste pedido.
            </p>
          ) : (
            itensPedido.map((item, index) => {
              const qtd = item.quantity ?? item.quantidade ?? 1;
              const unitario = item.unitPrice ?? item.preco ?? 0;
              const totalItem = item.total ?? unitario * qtd;

              return (
                <article
                  key={item.productId || item.id || index}
                  className={styles.item}
                >
                  <button
                    type="button"
                    className={styles.foto}
                    onClick={() => abrirProduto(item)}
                    disabled={!item.productId}
                    aria-label={`Ver ${obterNome(item)}`}
                  >
                    <img src={obterImagem(item)} alt={obterNome(item)} />
                  </button>

                  <div className={styles.itemInfo}>
                    <span className={styles.numero}>
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className={styles.nome}>
                      <h4>{obterNome(item)}</h4>
                      {item.variationText && (
                        <span className={styles.variacoes}>
                          {item.variationText}
                        </span>
                      )}
                      {(item.sellerSku || item.internalSku) && (
                        <span className={styles.sku}>
                          SKU: {item.sellerSku || item.internalSku}
                        </span>
                      )}
                    </div>
                    <div className={styles.itemDados}>
                      <div>
                        <span>Quantidade</span>
                        <strong>{qtd}</strong>
                      </div>
                      <div>
                        <span>Valor unitário</span>
                        <strong>{formatarValor(unitario)}</strong>
                      </div>
                      <div className={styles.totalItem}>
                        <span>Total do item</span>
                        <strong>{formatarValor(totalItem)}</strong>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </section>

      <section className={styles.secao}>
        <div className={styles.tituloSecao}>
          <span>ACOMPANHAMENTO</span>
          <h3>Informações da entrega</h3>
        </div>

        {!delivery ? (
          <p className={styles.estado}>
            Informações de entrega não disponíveis.
          </p>
        ) : (
          <div className={styles.gridEntrega}>
            {tipo === "venda"
              ? delivery.pickupCode && (
                  <div className={styles.codigoEntrega}>
                    <span>Código de coleta</span>
                    <strong>{delivery.pickupCode}</strong>
                  </div>
                )
              : delivery.confirmationCode && (
                  <div className={styles.codigoEntrega}>
                    <span>Código de recebimento</span>
                    <strong>{delivery.confirmationCode}</strong>
                  </div>
                )}
            {delivery.status && (
              <div className={styles.detalhe}>
                <span>Status da entrega</span>
                <strong>{formatarStatus(delivery.status)}</strong>
              </div>
            )}
            {delivery.carrier?.driver?.name && (
              <div className={styles.detalhe}>
                <span>Entregador</span>
                <strong>{delivery.carrier.driver.name}</strong>
              </div>
            )}
            {delivery.carrier?.driver?.phone && (
              <div className={styles.detalhe}>
                <span>Telefone</span>
                <strong>{delivery.carrier.driver.phone}</strong>
              </div>
            )}
            {delivery.carrier?.driver?.vehicle && (
              <div className={styles.detalhe}>
                <span>Veículo</span>
                <strong>{delivery.carrier.driver.vehicle}</strong>
              </div>
            )}
            {delivery.carrier?.driver?.plate && (
              <div className={styles.detalhe}>
                <span>Placa</span>
                <strong>{delivery.carrier.driver.plate}</strong>
              </div>
            )}
            {delivery.carrier?.trackingLink && (
              <div className={styles.detalhe}>
                <span>Rastreamento</span>
                <a
                  href={delivery.carrier.trackingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Acompanhar entrega
                </a>
              </div>
            )}
          </div>
        )}
      </section>

      <section className={styles.secao}>
        <div className={styles.tituloSecao}>
          <span>FINALIZAÇÃO</span>
          <h3>Pagamento e valores</h3>
        </div>

        <div className={styles.painelPagamento}>
          <div className={styles.detalhe}>
            <span>Forma de pagamento</span>
            <strong>{formatarPagamento(formaPagamento)}</strong>
          </div>
          {paymentDetails?.type && (
            <div className={styles.detalhe}>
              <span>Tipo</span>
              <strong>{paymentDetails.type}</strong>
            </div>
          )}
          {paymentDetails?.qrCodeUrl && (
            <div className={styles.detalhe}>
              <span>Pagamento via QR Code</span>
              <a
                href={paymentDetails.qrCodeUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Visualizar QR Code
              </a>
            </div>
          )}
        </div>

        <div className={styles.valores}>
          <div>
            <span>Subtotal</span>
            <strong>{formatarValor(subtotalCalculado)}</strong>
          </div>
          {shipping !== undefined && (
            <div>
              <span>Frete</span>
              <strong>{formatarValor(shipping)}</strong>
            </div>
          )}
          {interest !== undefined && (
            <div>
              <span>Juros</span>
              <strong>{formatarValor(interest)}</strong>
            </div>
          )}
          {discount !== undefined && (
            <div>
              <span>Desconto</span>
              <strong>- {formatarValor(discount)}</strong>
            </div>
          )}
          <div className={styles.totalFinal}>
            <span>Total do pedido</span>
            <strong>{formatarValor(totalCalculado)}</strong>
          </div>
        </div>
      </section>
    </div>
  );
};
